import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useToast } from "../hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Reusable field components ──────────────────────────────────────────

function TextField({ label, value, onChange, placeholder, disabled, required, type = "text", maxLength }) {
  return (
    <div>
      <label className="text-sm font-medium text-stone-700 block mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        type={type}
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        maxLength={maxLength}
        className={`w-full h-10 px-3 rounded-md border border-stone-200 text-sm bg-white
          focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent
          ${disabled ? "bg-stone-100 text-stone-500 cursor-not-allowed" : ""}`}
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, disabled, required, placeholder = "Seleciona..." }) {
  return (
    <div>
      <label className="text-sm font-medium text-stone-700 block mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <select
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        required={required}
        className={`w-full h-10 px-3 rounded-md border border-stone-200 text-sm bg-white
          focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent
          ${disabled ? "bg-stone-100 text-stone-400" : ""}`}
      >
        <option value="">{placeholder}</option>
        {options.map((opt, i) => (
          <option key={opt.id || i} value={opt.id}>{opt.descricao}</option>
        ))}
      </select>
    </div>
  );
}

function SectionHeader({ icon: Icon, children }) {
  return (
    <h2 className="text-lg font-bold text-stone-900 mb-4 flex items-center gap-2">
      {Icon && <Icon className="w-5 h-5 text-brand-600" />}
      {children}
    </h2>
  );
}

function PriceRow({ label, value }) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-stone-100 last:border-0">
      <span className="text-sm text-stone-600">{label}</span>
      <span className="text-sm font-semibold text-stone-900">{value || "\u2014"}</span>
    </div>
  );
}

// ── Icons (inline SVG) ─────────────────────────────────────────────────
const UserIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-brand-600">
    <path d="M10 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM3.465 14.493a1.23 1.23 0 0 0 .41 1.412A9.957 9.957 0 0 0 10 18c2.31 0 4.438-.784 6.131-2.1.43-.333.604-.903.408-1.41a7.002 7.002 0 0 0-13.074.003Z" />
  </svg>
);

const HomeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-brand-600">
    <path fillRule="evenodd" d="M9.293 2.293a1 1 0 0 1 1.414 0l7 7A1 1 0 0 1 17 11h-1v6a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1v-4h-2v4a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-6H3a1 1 0 0 1-.707-1.707l7-7Z" clipRule="evenodd" />
  </svg>
);

const SportIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-brand-600">
    <path d="M3.196 12.87l-.825.483a.75.75 0 0 0 0 1.294l7.25 4.25a.75.75 0 0 0 .758 0l7.25-4.25a.75.75 0 0 0 0-1.294l-.825-.484-5.666 3.322a2.25 2.25 0 0 1-2.276 0L3.196 12.87Z" />
    <path d="M3.196 8.87l-.825.483a.75.75 0 0 0 0 1.294l7.25 4.25a.75.75 0 0 0 .758 0l7.25-4.25a.75.75 0 0 0 0-1.294l-.825-.484-5.666 3.322a2.25 2.25 0 0 1-2.276 0L3.196 8.87Z" />
    <path d="M10.38 1.103a.75.75 0 0 0-.76 0l-7.25 4.25a.75.75 0 0 0 0 1.294l7.25 4.25a.75.75 0 0 0 .76 0l7.25-4.25a.75.75 0 0 0 0-1.294l-7.25-4.25Z" />
  </svg>
);

const GuardianIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-brand-600">
    <path fillRule="evenodd" d="M10 2a4 4 0 0 0-4 4v1H5a1 1 0 0 0-.994.89l-1 9A1 1 0 0 0 4 18h12a1 1 0 0 0 .994-1.11l-1-9A1 1 0 0 0 15 7h-1V6a4 4 0 0 0-4-4Zm2 5V6a2 2 0 1 0-4 0v1h4Zm-6 3a1 1 0 1 1 2 0 1 1 0 0 1-2 0Zm7-1a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z" clipRule="evenodd" />
  </svg>
);

const PaymentIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-brand-600">
    <path d="M3.75 3A1.75 1.75 0 0 0 2 4.75v.5h16v-.5A1.75 1.75 0 0 0 16.25 3H3.75ZM2 7.25v5.5A1.75 1.75 0 0 0 3.75 14.5h12.5a1.75 1.75 0 0 0 1.75-1.75v-5.5H2Z" />
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-brand-600">
    <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
  </svg>
);

// ── Helpers ────────────────────────────────────────────────────────────
function ageFromBirth(birthDate) {
  if (!birthDate) return null;
  try {
    const birth = new Date(birthDate + "T00:00:00");
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  } catch { return null; }
}

/** Idade FPA: idade a 31 de Dezembro do ano de fim da época.
 *  Ex: época "2026/2027" → referência 2027-12-31 */
function fpaAge(birthDate, seasonLabel) {
  if (!birthDate) return null;
  try {
    const birth = new Date(birthDate + "T00:00:00");
    const parts = (seasonLabel || "").split("/");
    const refYear = parseInt(parts[1]) || new Date().getFullYear();
    const ref = new Date(refYear, 11, 31); // 31 Dez
    let age = ref.getFullYear() - birth.getFullYear();
    if (ref.getMonth() < birth.getMonth() || (ref.getMonth() === birth.getMonth() && ref.getDate() < birth.getDate())) age--;
    return age;
  } catch { return null; }
}

function parseEscalaoRange(idadeStr) {
  if (!idadeStr) return null;
  const digits = (String(idadeStr).match(/\d+/g) || []).map(Number);
  if (digits.length === 1) return { lo: digits[0], hi: digits[0] };
  if (digits.length >= 2) return { lo: digits[0], hi: digits[1] };
  return null;
}

/** Encontra o escalão mais específico que contém a idade FPA (exclui Runner). */
function findEscalaoByAge(fpaAgeValue, escaloesList) {
  if (fpaAgeValue === null || !escaloesList?.length) return null;
  let best = null, bestSpan = null;
  for (const esc of escaloesList) {
    if ((esc.descricao || "").toLowerCase().includes("runner")) continue;
    const rng = parseEscalaoRange(esc.idade);
    if (!rng) continue;
    if (fpaAgeValue >= rng.lo && fpaAgeValue <= rng.hi) {
      const span = rng.hi - rng.lo;
      if (best === null || span < bestSpan) { best = esc; bestSpan = span; }
    }
  }
  return best;
}

/** Encontra o escalão Runner. */
function findRunnerEscalao(escaloesList) {
  if (!escaloesList?.length) return null;
  return escaloesList.find(e => (e.descricao || "").toLowerCase().includes("runner")) || null;
}

function formatMoney(v) {
  if (v === "" || v === null || v === undefined) return "\u2014";
  const n = parseFloat(v);
  if (isNaN(n)) return "\u2014";
  return n.toFixed(2) + " \u20AC";
}

// ── InscricoesPage ─────────────────────────────────────────────────────
const InscricoesPage = () => {
  const { toast } = useToast();

  const [tipo, setTipo] = useState("atleta");
  const [form, setForm] = useState({
    nome: "", email: "", birth_date: "", gender: "", phone: "", nif: "",
    documento_identificacao: "", numero_identificacao: "", validade_documento: "",
    nacionalidade: "Portuguesa", address: "", localidade: "", codigo_postal: "",
    modality: "", id_escalao: "", escalao: "", equipamento_tamanho: "",
    doencas_relevantes: "",
    ee_nif: "", ee_nome: "", ee_email: "", ee_phone: "", ee_relation: "", ee_gender: "",
    ee_address: "", ee_localidade: "", ee_codigo_postal: "",
    ee_documento_identificacao: "", ee_numero_identificacao: "", ee_birth_date: "",
    ee_validade_documento: "", ee_nacionalidade: "Portuguesa",
    pagamento: "mensal", pagamento_quota: "mensal",
    terms_accepted: false,
  });

  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const [eeChecking, setEeChecking] = useState(false);
  const [eeExists, setEeExists] = useState(false);
  const [eeData, setEeData] = useState(null);
  const [eeChecked, setEeChecked] = useState(false);

  const age = ageFromBirth(form.birth_date);
  const isMinor = age !== null && age < 18;

  // Dados extraídos das options (antes do return para usar na auto-atribuição)
  const seasonLabel = options?.season || "";
  const modalidades = options?.modalidades || [];
  const escaloes = options?.escaloes || [];
  const equipamentos = options?.escaloes_equipamentos || [];
  const generos = options?.generos || [];
  const docsIdent = options?.documentos_identificacao || [];

  // ── Auto-atribuição do escalão (regras FPA) ─────────────────────────
  const escalaoAuto = (() => {
    if (!options || tipo !== "atleta" || !form.birth_date || !form.modality) return null;
    const modalidade = modalidades.find(m => m.id === form.modality);
    const modDesc = (modalidade?.descricao || "").toLowerCase();
    // Runner: forçar escalão Runner
    if (modDesc.includes("runner")) {
      return findRunnerEscalao(escaloes);
    }
    // Academia (e restantes): calcular pela idade FPA
    const ageFpa = fpaAge(form.birth_date, seasonLabel);
    if (ageFpa !== null) return findEscalaoByAge(ageFpa, escaloes);
    return null;
  })();

  // Aplica o escalão automático quando muda a modalidade ou data nascimento
  useEffect(() => {
    if (escalaoAuto && escalaoAuto.id !== form.id_escalao) {
      update("id_escalao", escalaoAuto.id);
      update("escalao", escalaoAuto.descricao || "");
    }
  }, [form.modality, form.birth_date, tipo, options, escalaoAuto, form.id_escalao]);

  useEffect(() => {
    axios.get(`${API}/inscricoes/options`)
      .then(res => { setOptions(res.data); setLoading(false); })
      .catch(() => { setError("Erro ao carregar opções do formulário."); setLoading(false); });
  }, []);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const selectedPrices = (() => {
    if (!options || !form.id_escalao) return null;
    return options.escalao_precos?.find(p => p.id_escalao === form.id_escalao) || null;
  })();

  const quotaValue = (() => {
    if (!options || !form.birth_date) return "";
    const a = ageFromBirth(form.birth_date);
    if (a === null) return "";
    const quotas = [...(options.valores_quotas || [])].sort(
      (x, y) => parseInt(x.idade_maxima) - parseInt(y.idade_maxima)
    );
    for (const q of quotas) {
      if (a <= parseInt(q.idade_maxima)) return q.valor;
    }
    return quotas.length ? quotas[quotas.length - 1].valor : "";
  })();

  // Quota de socio do Encarregado de Educacao (quando ainda nao e socio)
  const eeQuotaValue = (() => {
    if (!options || !form.ee_birth_date) return "";
    const a = ageFromBirth(form.ee_birth_date);
    if (a === null) return "";
    const quotas = [...(options.valores_quotas || [])].sort(
      (x, y) => parseInt(x.idade_maxima) - parseInt(y.idade_maxima)
    );
    for (const q of quotas) {
      if (a <= parseInt(q.idade_maxima)) return q.valor;
    }
    return quotas.length ? quotas[quotas.length - 1].valor : "";
  })();

  const checkEE = useCallback(async () => {
    const nif = (form.ee_nif || "").trim();
    if (!nif || nif.length < 5) return;
    setEeChecking(true);
    setEeChecked(false);
    try {
      const res = await axios.get(`${API}/inscricoes/ee-check`, { params: { nif } });
      setEeExists(res.data.exists);
      setEeData(res.data);
    } catch { setEeExists(false); setEeData(null); }
    finally { setEeChecking(false); setEeChecked(true); }
  }, [form.ee_nif]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.terms_accepted) {
      toast({ title: "Termos e condições", description: "Tens de aceitar os termos e condições.", variant: "destructive" });
      return;
    }
    const missing = [];
    if (!(form.gender || "").trim()) missing.push("género");
    if (!(form.phone || "").trim()) missing.push("telefone");
    if (!(form.nif || "").trim()) missing.push("NIF");
    if (!(form.documento_identificacao || "").trim()) missing.push("documento de identificação");
    if (!(form.numero_identificacao || "").trim()) missing.push("nº do documento");
    if (!(form.validade_documento || "").trim()) missing.push("validade do documento");
    if (!(form.nacionalidade || "").trim()) missing.push("nacionalidade");
    if (!(form.address || "").trim()) missing.push("morada");
    if (!(form.localidade || "").trim()) missing.push("localidade");
    if (!(form.codigo_postal || "").trim()) missing.push("código postal");
    if (missing.length) {
      toast({ title: "Campos obrigatórios", description: `Preenche os campos: ${missing.join(", ")}.`, variant: "destructive" });
      return;
    }
    if (isMinor && !(form.ee_nif || "").trim()) {
      toast({ title: "Encarregado de Educação", description: "O NIF do Encarregado de Educação é obrigatório para menores de 18 anos.", variant: "destructive" });
      return;
    }
    if (isMinor && !eeChecked) {
      toast({ title: "Encarregado de Educação", description: "Clica em \"Verificar\" para confirmar o NIF do Encarregado de Educação.", variant: "destructive" });
      return;
    }
    if (isMinor && !eeExists) {
      const missingEE = [];
      if (!(form.ee_nome || "").trim()) missingEE.push("nome");
      if (!(form.ee_email || "").trim()) missingEE.push("email");
      if (!(form.ee_birth_date || "").trim()) missingEE.push("data de nascimento");
      if (!(form.ee_phone || "").trim()) missingEE.push("telefone");
      if (!(form.ee_relation || "").trim()) missingEE.push("parentesco");
      if (!(form.ee_gender || "").trim()) missingEE.push("género");
      if (!(form.ee_documento_identificacao || "").trim()) missingEE.push("documento de identificação");
      if (!(form.ee_numero_identificacao || "").trim()) missingEE.push("nº do documento");
      if (!(form.ee_validade_documento || "").trim()) missingEE.push("validade do documento");
      if (!(form.ee_nacionalidade || "").trim()) missingEE.push("nacionalidade");
      if (!(form.ee_address || "").trim()) missingEE.push("morada");
      if (!(form.ee_localidade || "").trim()) missingEE.push("localidade");
      if (!(form.ee_codigo_postal || "").trim()) missingEE.push("código postal");
      if (missingEE.length) {
        toast({
          title: "Encarregado de Educação",
          description: `Para registar um novo Encarregado de Educação, preenche: ${missingEE.join(", ")}.`,
          variant: "destructive",
        });
        return;
      }
    }
    setSubmitting(true);
    setError(null);
    try {
      // Remove empty strings de campos opcionais para evitar erros de validação (ex: EmailStr vazio)
      const cleanPayload = {};
      for (const [k, v] of Object.entries({ ...form, tipo, ee_exists: eeExists, birth_date: form.birth_date || "" })) {
        cleanPayload[k] = v === "" ? undefined : v;
      }
      const res = await axios.post(`${API}/inscricoes`, cleanPayload);
      setSuccess(res.data || { id: "—" });
      toast({ title: "Inscrição submetida!", description: "A tua inscrição foi submetida com sucesso e encontra-se em análise." });
    } catch (err) {
      const detail = err.response?.data?.detail || "Erro ao submeter a inscrição.";
      setError(detail);
      toast({ title: "Erro", description: detail, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <main className="pt-32 pb-16 min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-brand-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-stone-600">A carregar formulário...</p>
        </div>
      </main>
    );
  }

  if (error && !options) {
    return (
      <main className="pt-32 pb-16 min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <p className="text-red-600 font-semibold mb-2">Erro</p>
          <p className="text-stone-600">{error}</p>
        </div>
      </main>
    );
  }

  // ── Success ──────────────────────────────────────────────────────────
  if (success) {
    return (
      <main className="pt-32 pb-16 min-h-screen bg-stone-50">
        <div className="max-w-2xl mx-auto px-6">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg border border-stone-100 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckIcon />
            </div>
            <h1 className="text-2xl font-bold text-stone-900 mb-3">Inscrição submetida com sucesso!</h1>
            <p className="text-stone-600 mb-6">
              O teu pedido de inscrição foi submetido e encontra-se <strong>em análise</strong>.
              Irás receber um email de confirmação com os detalhes.
            </p>
            <div className="bg-stone-50 rounded-xl p-5 text-left mb-6">
              <p className="text-sm text-stone-500 mb-1">Referência</p>
              <p className="font-mono text-sm text-stone-900 break-all">{success?.id || "—"}</p>
              <p className="text-sm text-stone-500 mt-3 mb-1">Estado</p>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-medium border border-amber-200">
                Pendente
              </span>
            </div>
            <p className="text-sm text-stone-500">
              Assim que a inscrição for aprovada, irás receber um email com os dados de acesso à Área de Sócio.
            </p>
          </div>
        </div>
      </main>
    );
  }

  // ── Form ─────────────────────────────────────────────────────────────
  const regulamentoUrl = options?.regulamento_url || `${API}/regulamento`;

  return (
    <main className="pt-28 pb-20 bg-stone-50 min-h-screen">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <div className="mb-10 text-center md:text-left">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-50 border border-brand-200 text-brand-700 text-xs font-medium tracking-wide mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5">
              <path fillRule="evenodd" d="M4 2a1.5 1.5 0 0 0-1.5 1.5v9A1.5 1.5 0 0 0 4 14h8a1.5 1.5 0 0 0 1.5-1.5V6.621a1.5 1.5 0 0 0-.44-1.06L9.94 2.439A1.5 1.5 0 0 0 8.878 2H4Zm1 5.75A.75.75 0 0 1 5.75 7h4.5a.75.75 0 0 1 0 1.5h-4.5A.75.75 0 0 1 5 7.75Zm0 3a.75.75 0 0 1 .75-.75h4.5a.75.75 0 0 1 0 1.5h-4.5a.75.75 0 0 1-.75-.75Z" clipRule="evenodd" />
            </svg>
            Formulário de inscrição — Época {seasonLabel}
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-stone-900">
            Nova Inscrição
          </h1>
          <p className="mt-3 text-stone-500 text-[15px] max-w-2xl">
            Preenche o formulário abaixo para te inscreveres como sócio{tipo === "atleta" ? " e atleta" : ""} do CATV.
          </p>
          <a
            href={options?.regulamento_atletismo_url || `${API}/regulamento-atletismo`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-2 text-sm text-brand-600 hover:text-brand-700 font-medium transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-4 h-4">
              <path fillRule="evenodd" d="M4 2a1.5 1.5 0 0 0-1.5 1.5v9A1.5 1.5 0 0 0 4 14h8a1.5 1.5 0 0 0 1.5-1.5V6.621a1.5 1.5 0 0 0-.44-1.06L9.94 2.439A1.5 1.5 0 0 0 8.878 2H4Zm1 5.75A.75.75 0 0 1 5.75 7h4.5a.75.75 0 0 1 0 1.5h-4.5A.75.75 0 0 1 5 7.75Zm0 3a.75.75 0 0 1 .75-.75h4.5a.75.75 0 0 1 0 1.5h-4.5a.75.75 0 0 1-.75-.75Z" clipRule="evenodd" />
            </svg>
            Ver Regulamento de Atletismo {seasonLabel}
          </a>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* ── Tipo ─────────────────────────────────────────────────── */}
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-stone-100">
            <SectionHeader icon={UserIcon}>Tipo de inscrição</SectionHeader>
            <div className="flex flex-wrap gap-4">
              {["atleta", "socio"].map(t => {
                const isAtletaDisabled = t === "atleta" && age !== null && age < 5;
                return (
                <label key={t} className={`flex-1 min-w-[180px] cursor-pointer rounded-xl border-2 p-4 transition-all
                  ${tipo === t ? "border-brand-500 bg-brand-50" : "border-stone-200 hover:border-stone-300"}
                  ${isAtletaDisabled ? "opacity-50 cursor-not-allowed" : ""}`}>
                  <input type="radio" name="tipo" value={t} checked={tipo === t}
                    onChange={() => setTipo(t)} className="sr-only" disabled={isAtletaDisabled} />
                  <p className="font-semibold text-stone-900">{t === "atleta" ? "Sócio e Atleta" : "Sócio"}</p>
                  <p className="text-xs text-stone-500 mt-1">
                    {isAtletaDisabled ? "Idade mínima: 5 anos" : t === "atleta" ? "Inscrição com prática desportiva" : "Apenas quota de sócio"}
                  </p>
                </label>
                );
              })}
            </div>
          </div>

          {/* ── Dados Pessoais ──────────────────────────────────────── */}
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-stone-100">
            <SectionHeader icon={UserIcon}>Dados {tipo === "atleta" ? "do Atleta" : "do Sócio"}</SectionHeader>
            <div className="grid sm:grid-cols-2 gap-4">
              <TextField label="Nome completo" value={form.nome} onChange={v => update("nome", v)} required placeholder="Nome completo" maxLength={200} />
              <TextField label="Email" value={form.email} onChange={v => update("email", v)} required type="email" placeholder="email@exemplo.com" />
              <TextField label="Data de nascimento" value={form.birth_date} onChange={v => update("birth_date", v)} required type="date" />
              <SelectField label="Género" value={form.gender} onChange={v => update("gender", v)} options={generos} required />
              <TextField label="Telefone" value={form.phone} onChange={v => update("phone", v)} type="tel" placeholder="912345678" maxLength={30} required />
              <TextField label="NIF" value={form.nif} onChange={v => update("nif", v)} placeholder="123456789" maxLength={20} required />
              <SelectField label="Documento de identificação" value={form.documento_identificacao}
                onChange={v => update("documento_identificacao", v)} options={docsIdent} required />
              <TextField label="Nº documento" value={form.numero_identificacao}
                onChange={v => update("numero_identificacao", v)} placeholder="Número" maxLength={50} required />
              <TextField label="Validade do documento" value={form.validade_documento}
                onChange={v => update("validade_documento", v)} type="date" required />
              <TextField label="Nacionalidade" value={form.nacionalidade}
                onChange={v => update("nacionalidade", v)} placeholder="Portuguesa" maxLength={50} required />
            </div>
          </div>

          {/* ── Morada ──────────────────────────────────────────────── */}
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-stone-100">
            <SectionHeader icon={HomeIcon}>Morada</SectionHeader>
            <div className="grid sm:grid-cols-2 gap-4">
              <TextField label="Morada" value={form.address} onChange={v => update("address", v)} placeholder="Rua, nº" maxLength={300} required />
              <TextField label="Localidade" value={form.localidade} onChange={v => update("localidade", v)} placeholder="Localidade" maxLength={100} required />
              <TextField label="Código Postal" value={form.codigo_postal} onChange={v => update("codigo_postal", v)} placeholder="1234-567" maxLength={20} required />
            </div>
          </div>

          {/* ── Dados Desportivos (só atleta) ───────────────────────── */}
          {tipo === "atleta" && (
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-stone-100">
              <SectionHeader icon={SportIcon}>Dados Desportivos</SectionHeader>
              <div className="grid sm:grid-cols-2 gap-4">
                <SelectField label="Modalidade" value={form.modality} onChange={v => update("modality", v)}
                  options={modalidades} required />
                <div>
                  <label className="text-sm font-medium text-stone-700 block mb-1">
                    Escalão<span className="text-red-500 ml-0.5">*</span>
                  </label>
                  <select
                    value={form.id_escalao || ""}
                    onChange={e => { const v = e.target.value; const esc = escaloes.find(es => es.id === v); update("id_escalao", v); update("escalao", esc?.descricao || ""); }}
                    required
                    disabled
                    className="w-full h-10 px-3 rounded-md border border-stone-200 text-sm bg-stone-100 text-stone-500 cursor-not-allowed
                      focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  >
                    <option value="">Seleciona...</option>
                    {escaloes.map((opt, i) => (
                      <option key={opt.id || i} value={opt.id}>
                        {opt.descricao}{opt.idade ? ` (${opt.idade})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <SelectField label="Tamanho equipamento" value={form.equipamento_tamanho}
                  onChange={v => update("equipamento_tamanho", v)} options={equipamentos} />
              </div>
              <div className="mt-4">
                <label className="text-sm font-medium text-stone-700 block mb-1">Doenças relevantes / Observações</label>
                <textarea value={form.doencas_relevantes || ""}
                  onChange={e => update("doencas_relevantes", e.target.value)}
                  placeholder="Alergias, condições médicas, etc." maxLength={1000} rows={3}
                  className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm bg-white
                    focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-y" />
              </div>
            </div>
          )}

          {/* ── Encarregado de Educação (menores) ──────────────────── */}
          {isMinor && (
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-stone-100">
              <SectionHeader icon={GuardianIcon}>Encarregado de Educação</SectionHeader>
              <p className="text-sm text-stone-500 mb-4">
                Por teres menos de 18 anos, é obrigatório identificar o teu Encarregado de Educação.
              </p>
              <div className="flex gap-3 mb-4">
                <div className="flex-1">
                  <TextField label="NIF do Encarregado de Educação" value={form.ee_nif}
                    onChange={v => { update("ee_nif", v); setEeExists(false); setEeData(null); setEeChecked(false); }}
                    placeholder="NIF" maxLength={20} required />
                </div>
                <div className="flex items-end pb-0.5">
                  <button type="button" onClick={checkEE} disabled={eeChecking || !(form.ee_nif || "").trim()}
                    className="h-10 px-4 rounded-md bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium
                      disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                    {eeChecking ? "A verificar..." : "Verificar"}
                  </button>
                </div>
              </div>
              {eeExists && eeData && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                  <p className="text-sm text-green-800 font-medium">✅ Encarregado de Educação já é sócio</p>
                  <p className="text-sm text-green-700 mt-1">{eeData.nome} — Sócio nº {eeData.member_number}</p>
                </div>
              )}
              {eeChecked && !eeChecking && form.ee_nif?.trim() && !eeExists && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                  <p className="text-sm text-amber-800">Encarregado de Educação não encontrado. Preenche os dados abaixo para o registar.</p>
                </div>
              )}
              {eeChecked && !eeChecking && form.ee_nif?.trim() && !eeExists && (
                <div className="grid sm:grid-cols-2 gap-4">
                  <TextField label="Nome completo" value={form.ee_nome} onChange={v => update("ee_nome", v)} placeholder="Nome" maxLength={200} required />
                  <TextField label="Email" value={form.ee_email} onChange={v => update("ee_email", v)} type="email" placeholder="email@exemplo.com" required />
                  <TextField label="Telefone" value={form.ee_phone} onChange={v => update("ee_phone", v)} type="tel" placeholder="912345678" maxLength={30} required />
                  <TextField label="Data de nascimento" value={form.ee_birth_date} onChange={v => update("ee_birth_date", v)} type="date" required />
                  <SelectField label="Género" value={form.ee_gender} onChange={v => update("ee_gender", v)} options={generos} required />
                  <TextField label="Parentesco" value={form.ee_relation} onChange={v => update("ee_relation", v)} placeholder="Pai, Mãe, Tutor..." maxLength={50} required />
                  <SelectField label="Documento identificação" value={form.ee_documento_identificacao}
                    onChange={v => update("ee_documento_identificacao", v)} options={docsIdent} required />
                  <TextField label="Nº documento" value={form.ee_numero_identificacao}
                    onChange={v => update("ee_numero_identificacao", v)} placeholder="Número" maxLength={50} required />
                  <TextField label="Validade do documento" value={form.ee_validade_documento}
                    onChange={v => update("ee_validade_documento", v)} type="date" required />
                  <TextField label="Nacionalidade" value={form.ee_nacionalidade}
                    onChange={v => update("ee_nacionalidade", v)} placeholder="Portuguesa" maxLength={50} required />
                  <TextField label="Morada" value={form.ee_address} onChange={v => update("ee_address", v)} placeholder="Morada" maxLength={300} required />
                  <TextField label="Localidade" value={form.ee_localidade} onChange={v => update("ee_localidade", v)} placeholder="Localidade" maxLength={100} required />
                  <TextField label="Código Postal" value={form.ee_codigo_postal} onChange={v => update("ee_codigo_postal", v)} placeholder="1234-567" maxLength={20} required />
                </div>
              )}
            </div>
          )}

          {/* ── Pagamento ───────────────────────────────────────────── */}
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-stone-100">
            <SectionHeader icon={PaymentIcon}>Pagamento</SectionHeader>
            {tipo === "atleta" && (
              <div className="mb-6">
                <label className="text-sm font-medium text-stone-700 block mb-2">Pagamento da modalidade</label>
                <div className="flex gap-4">
                  {["mensal", "anual"].map(p => (
                    <label key={p} className={`flex-1 cursor-pointer rounded-xl border-2 p-3 text-center transition-all
                      ${form.pagamento === p ? "border-brand-500 bg-brand-50" : "border-stone-200 hover:border-stone-300"}`}>
                      <input type="radio" name="pagamento" value={p} checked={form.pagamento === p}
                        onChange={() => update("pagamento", p)} className="sr-only" />
                      <p className="font-semibold text-stone-900 text-sm">{p === "mensal" ? "Mensal" : "Anual"}</p>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="mb-6">
              <label className="text-sm font-medium text-stone-700 block mb-2">Pagamento da quota de sócio</label>
              <div className="flex gap-4">
                {["mensal", "anual"].map(p => (
                  <label key={p} className={`flex-1 cursor-pointer rounded-xl border-2 p-3 text-center transition-all
                    ${form.pagamento_quota === p ? "border-brand-500 bg-brand-50" : "border-stone-200 hover:border-stone-300"}`}>
                    <input type="radio" name="pagamento_quota" value={p} checked={form.pagamento_quota === p}
                      onChange={() => update("pagamento_quota", p)} className="sr-only" />
                    <p className="font-semibold text-stone-900 text-sm">{p === "mensal" ? "Mensal" : "Anual"}</p>
                  </label>
                ))}
              </div>
            </div>
            <div className="bg-stone-50 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-stone-900 mb-3">Resumo de valores</h3>
              {tipo === "atleta" && selectedPrices && (
                <>
                  {selectedPrices.inscricao && <PriceRow label="Inscrição" value={formatMoney(selectedPrices.inscricao)} />}
                  {selectedPrices.seguro && <PriceRow label="Seguro" value={formatMoney(selectedPrices.seguro)} />}
                  {form.pagamento === "anual"
                    ? selectedPrices.anuidade && <PriceRow label="Anuidade" value={formatMoney(selectedPrices.anuidade)} />
                    : selectedPrices.mensalidade && <PriceRow label="Modalidade (mensal)" value={formatMoney(selectedPrices.mensalidade)} />
                  }
                </>
              )}
              {quotaValue && (
                <PriceRow label={`Quota de sócio (${form.pagamento_quota === "anual" ? "anual" : "mensal"})`}
                  value={formatMoney(form.pagamento_quota === "anual" ? (parseFloat(quotaValue || 0) * 12).toFixed(2) : quotaValue)} />
              )}
              {isMinor && eeChecked && !eeExists && eeQuotaValue && (
                <PriceRow label={`Quota de sócio do Encarregado de Educação (${form.pagamento_quota === "anual" ? "anual" : "mensal"})`}
                  value={formatMoney(form.pagamento_quota === "anual" ? (parseFloat(eeQuotaValue || 0) * 12).toFixed(2) : eeQuotaValue)} />
              )}
              {(!selectedPrices && !quotaValue) && (
                <p className="text-sm text-stone-400">Seleciona o escalão e preenche a data de nascimento para ver os valores.</p>
              )}
            </div>
          </div>

          {/* ── Termos ──────────────────────────────────────────────── */}
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-stone-100">
            <div className="flex items-start gap-3">
              <input type="checkbox" id="terms" checked={form.terms_accepted}
                onChange={e => update("terms_accepted", e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-stone-300 text-brand-600 focus:ring-brand-500" />
              <label htmlFor="terms" className="text-sm text-stone-600 leading-relaxed">
                Declaro que li e aceito o{" "}
                <a href={regulamentoUrl} target="_blank" rel="noopener noreferrer"
                  className="text-brand-600 underline hover:text-brand-700 font-medium">Regulamento</a>
                {tipo === "atleta" && (
                  <>{' '}e o{' '}
                    <a href={options?.regulamento_atletismo_url || `${API}/regulamento-atletismo`} target="_blank" rel="noopener noreferrer"
                      className="text-brand-600 underline hover:text-brand-700 font-medium">Regulamento de Atletismo</a>
                  </>
                )}
                {" "}do CATV para a época {seasonLabel}. <span className="text-red-500">*</span>
              </label>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-center gap-4">
            <button type="submit" disabled={submitting}
              className="w-full sm:w-auto px-8 py-3.5 rounded-full bg-gradient-to-r from-brand-600 to-brand-700
                hover:from-brand-700 hover:to-brand-800 text-white font-bold text-[15px] transition-all
                shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              {submitting ? (
                <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />A submeter...</>
              ) : "Submeter inscrição"}
            </button>
            <p className="text-xs text-stone-400 text-center sm:text-left">
              Ao submeteres, o teu pedido será analisado pela equipa do CATV. Receberás um email de confirmação.
            </p>
          </div>
        </form>
      </div>
    </main>
  );
};

export default InscricoesPage;
